import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { 
  ArrowLeft, Heart, Sparkles, Send, MessageCircle,
  Lightbulb, RefreshCw, Image, X, Upload, Star, Check,
  Plus, Pencil, Trash2, Wand2, Brain, Zap, Copy, Target, User
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Toaster, toast } from "sonner";
import { useTheme } from "../contexts/ThemeContext";

const TONE_OPTIONS = [
  { value: "friendly", label: "Friendly", description: "Warm and approachable" },
  { value: "flirty", label: "Flirty", description: "Playful and charming" },
  { value: "supportive", label: "Supportive", description: "Encouraging and caring" },
  { value: "witty", label: "Witty", description: "Clever and humorous" },
  { value: "casual", label: "Casual", description: "Relaxed and informal" },
  { value: "neurodiverse", label: "Neuro-Diverse", description: "Direct, literal, clear" },
  { value: "lovebomb", label: "BPD-Lovebomb", description: "Intensely adoring" },
  { value: "professional", label: "Professional", description: "Polished and respectful" },
  { value: "romantic", label: "Romantic", description: "Poetic and heartfelt" },
  { value: "mysterious", label: "Mysterious", description: "Enigmatic and intriguing" }
];

const ErosAdvice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isCyber = theme === "agent_eros_1";
  const [activeTab, setActiveTab] = useState("advice");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState("");
  const fileInputRef = useRef(null);
  const adviceInputRef = useRef(null);
  const commentInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  
  // Advice state with session memory
  const [adviceQuestion, setAdviceQuestion] = useState(location.state?.prompt || "");
  const [adviceSessionId, setAdviceSessionId] = useState(null);
  const [adviceMessages, setAdviceMessages] = useState([]); // [{role: 'user'|'assistant', content: string}]
  
  // Comment suggestions state
  const [postContent, setPostContent] = useState("");
  const [platform, setPlatform] = useState("");
  const [tone, setTone] = useState("friendly");
  const [commentMode, setCommentMode] = useState("text"); // text or image
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageContext, setImageContext] = useState("");
  
  // Tone preferences
  const [favoriteTones, setFavoriteTones] = useState([]);
  const [defaultTone, setDefaultTone] = useState("friendly");
  const [showTonePresets, setShowTonePresets] = useState(false);
  const [availableTones, setAvailableTones] = useState(TONE_OPTIONS);
  const [customTones, setCustomTones] = useState([]);
  
  // Custom tone creation/editing
  const [showCustomToneDialog, setShowCustomToneDialog] = useState(false);
  const [editingTone, setEditingTone] = useState(null);
  const [customToneName, setCustomToneName] = useState("");
  const [customToneDescription, setCustomToneDescription] = useState("");
  const [customTonePrompt, setCustomTonePrompt] = useState("");
  const [savingCustomTone, setSavingCustomTone] = useState(false);
  
  // Match selection
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(location.state?.selectedMatch || "");
  const [includeMyData, setIncludeMyData] = useState(false);
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [recentMatches, setRecentMatches] = useState([]);

  // Load user's tone preferences and matches on mount
  useEffect(() => {
    loadTonePreferences();
    loadMatches();
    loadRecentMatches();
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && adviceMessages.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [adviceMessages]);

  // Generate a new session ID for advice chat
  const startNewAdviceSession = () => {
    const newSessionId = `advice_${user?.user_id || 'anon'}_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    setAdviceSessionId(newSessionId);
    setAdviceMessages([]);
    setAdviceQuestion("");
    setResponse("");
  };

  const loadRecentMatches = () => {
    try {
      const stored = localStorage.getItem("eros_recent_matches");
      if (stored) {
        setRecentMatches(JSON.parse(stored));
      }
    } catch (error) {
      console.error("Failed to load recent matches:", error);
    }
  };

  const addToRecentMatches = (matchId) => {
    if (!matchId || matchId === "none") return;
    
    try {
      let recent = [...recentMatches];
      // Remove if already exists
      recent = recent.filter(id => id !== matchId);
      // Add to front
      recent.unshift(matchId);
      // Keep only last 5
      recent = recent.slice(0, 5);
      
      setRecentMatches(recent);
      localStorage.setItem("eros_recent_matches", JSON.stringify(recent));
    } catch (error) {
      console.error("Failed to save recent match:", error);
    }
  };

  const handleMatchSelect = (matchId) => {
    setSelectedMatch(matchId);
    addToRecentMatches(matchId);
  };

  const loadMatches = async () => {
    try {
      const res = await authAxios.get("/matches");
      setMatches(res.data || []);
    } catch (error) {
      console.error("Failed to load matches:", error);
    } finally {
      setLoadingMatches(false);
    }
  };

  const loadTonePreferences = async () => {
    try {
      const res = await authAxios.get("/preferences/tones");
      setFavoriteTones(res.data.favorite_tones || []);
      setDefaultTone(res.data.default_tone || "friendly");
      setTone(res.data.default_tone || "friendly");
      setCustomTones(res.data.custom_tones || []);
      
      // Merge built-in and custom tones
      const builtInTones = TONE_OPTIONS;
      const customToneOptions = (res.data.custom_tones || []).map(t => ({
        value: t.tone_id,
        label: t.name,
        description: t.description || "Custom tone",
        custom: true,
        prompt: t.prompt
      }));
      setAvailableTones([...builtInTones, ...customToneOptions]);
    } catch (error) {
      console.error("Failed to load tone preferences:", error);
    }
  };

  const toggleFavoriteTone = async (toneValue) => {
    try {
      const res = await authAxios.post("/preferences/tones/favorite", { tone: toneValue });
      setFavoriteTones(res.data.favorite_tones);
      toast.success(res.data.message);
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  const setAsDefaultTone = async (toneValue) => {
    try {
      await authAxios.put("/preferences/tones", { default_tone: toneValue });
      setDefaultTone(toneValue);
      const toneName = availableTones.find(t => t.value === toneValue)?.label;
      toast.success(`${toneName} set as default`);
    } catch (error) {
      toast.error("Failed to set default tone");
    }
  };

  // Custom tone management
  const openCreateToneDialog = () => {
    setEditingTone(null);
    setCustomToneName("");
    setCustomToneDescription("");
    setCustomTonePrompt("");
    setShowCustomToneDialog(true);
  };

  const openEditToneDialog = (tone) => {
    setEditingTone(tone);
    setCustomToneName(tone.name || "");
    setCustomToneDescription(tone.description || "");
    setCustomTonePrompt(tone.prompt || "");
    setShowCustomToneDialog(true);
  };

  const saveCustomTone = async () => {
    if (!customToneName.trim()) {
      toast.error("Please enter a tone name");
      return;
    }
    if (!customTonePrompt.trim()) {
      toast.error("Please enter tone instructions");
      return;
    }

    setSavingCustomTone(true);
    try {
      if (editingTone) {
        // Update existing tone
        await authAxios.put(`/tones/custom/${editingTone.tone_id}`, {
          name: customToneName,
          description: customToneDescription,
          prompt: customTonePrompt
        });
        toast.success("Custom tone updated!");
      } else {
        // Create new tone
        await authAxios.post("/tones/custom", {
          name: customToneName,
          description: customToneDescription,
          prompt: customTonePrompt
        });
        toast.success("Custom tone created!");
      }
      setShowCustomToneDialog(false);
      loadTonePreferences(); // Reload tones
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save custom tone");
    } finally {
      setSavingCustomTone(false);
    }
  };

  const deleteCustomTone = async (toneId) => {
    if (!window.confirm("Are you sure you want to delete this custom tone?")) return;
    
    try {
      await authAxios.delete(`/tones/custom/${toneId}`);
      toast.success("Custom tone deleted");
      if (tone === toneId) {
        setTone("friendly");
      }
      loadTonePreferences();
    } catch (error) {
      toast.error("Failed to delete custom tone");
    }
  };

  const handleGetAdvice = async () => {
    if (!adviceQuestion.trim()) {
      toast.error("Please enter your question");
      return;
    }
    
    // Generate session ID if this is the first message
    let currentSessionId = adviceSessionId;
    if (!currentSessionId) {
      currentSessionId = `advice_${user?.user_id || 'anon'}_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      setAdviceSessionId(currentSessionId);
    }
    
    // Add user message to conversation immediately
    const userMessage = { role: 'user', content: adviceQuestion };
    setAdviceMessages(prev => [...prev, userMessage]);
    
    setLoading(true);
    setResponse("");
    const questionText = adviceQuestion;
    setAdviceQuestion(""); // Clear input immediately
    
    try {
      const res = await authAxios.post("/ai/chat", {
        message: questionText,
        context: "advice",
        match_id: selectedMatch && selectedMatch !== "none" ? selectedMatch : null,
        include_user_data: includeMyData,
        session_id: currentSessionId
      });
      
      // Add AI response to conversation
      const aiMessage = { role: 'assistant', content: res.data.response };
      setAdviceMessages(prev => [...prev, aiMessage]);
      setResponse(res.data.response);
    } catch (error) {
      toast.error("Failed to get advice");
      // Remove the user message if request failed
      setAdviceMessages(prev => prev.slice(0, -1));
      setAdviceQuestion(questionText); // Restore the question
    } finally {
      setLoading(true);
      setLoading(false);
      // Auto-focus input after response
      setTimeout(() => adviceInputRef.current?.focus(), 100);
    }
  };

  const handleGetCommentSuggestions = async () => {
    if (!postContent.trim()) {
      toast.error("Please enter the post content");
      return;
    }
    
    setLoading(true);
    setResponse("");
    
    try {
      const res = await authAxios.post("/ai/comment-suggestions", {
        post_content: postContent,
        platform: platform || "social media",
        tone,
        match_id: selectedMatch && selectedMatch !== "none" ? selectedMatch : null,
        include_user_data: includeMyData
      });
      
      setResponse(res.data.suggestions);
    } catch (error) {
      toast.error("Failed to generate suggestions");
    } finally {
      setLoading(false);
      // Auto-focus input after response
      setTimeout(() => commentInputRef.current?.focus(), 100);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be less than 10MB");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setImagePreview(event.target.result);
      // Extract base64 data without the prefix
      const base64 = event.target.result.split(",")[1];
      setImageBase64(base64);
      setImageUrl("");
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImagePreview(null);
    setImageBase64("");
    setImageUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAnalyzeImage = async () => {
    if (!imageBase64 && !imageUrl) {
      toast.error("Please upload an image or provide an image URL");
      return;
    }
    
    setLoading(true);
    setResponse("");
    
    try {
      const res = await authAxios.post("/ai/analyze-image-for-comments", {
        image_base64: imageBase64,
        image_url: imageUrl,
        platform: platform || "social media",
        tone,
        context: imageContext,
        match_id: selectedMatch && selectedMatch !== "none" ? selectedMatch : null,
        include_user_data: includeMyData
      });
      
      setResponse(res.data.suggestions);
    } catch (error) {
      toast.error("Failed to analyze image");
    } finally {
      setLoading(false);
      // Auto-focus input after response (focus comment input for image analysis tab)
      setTimeout(() => commentInputRef.current?.focus(), 100);
    }
  };

  return (
    <div className="min-h-screen bg-divine-black">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button
            data-testid="back-btn"
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-white/60 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            {isCyber ? (
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-400/30 blur-md rounded-full" />
                <Brain className="w-6 h-6 text-cyan-400 relative" />
              </div>
            ) : (
              <Heart className="w-6 h-6 text-divine-red fill-divine-red" />
            )}
            <div>
              <h1 className={isCyber ? "text-xl text-white font-semibold" : "font-cinzel text-xl text-white"}>
                Social Guidance
              </h1>
              <p className="text-white/40 text-sm">
                {isCyber ? "Emotional Intelligence powered by AI" : "Ancient wisdom for modern times"}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-4 mb-8">
          <button
            data-testid="advice-tab"
            onClick={() => { setActiveTab("advice"); setResponse(""); }}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-all ${
              isCyber 
                ? activeTab === "advice"
                  ? "bg-gradient-to-r from-cyan-500 to-purple-500 text-white glow-effect font-semibold"
                  : "bg-white/5 text-white/60 hover:bg-white/10"
                : activeTab === "advice"
                  ? "bg-divine-red text-white btn-glow font-cinzel rounded-full"
                  : "bg-white/5 text-white/60 hover:bg-white/10 font-cinzel rounded-full"
            }`}
          >
            {isCyber ? <Zap className="w-4 h-4" /> : <Heart className="w-4 h-4" />}
            Guidance
          </button>
          <button
            data-testid="comments-tab"
            onClick={() => { setActiveTab("comments"); setResponse(""); }}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-all ${
              isCyber 
                ? activeTab === "comments"
                  ? "bg-gradient-to-r from-cyan-500 to-purple-500 text-white glow-effect font-semibold"
                  : "bg-white/5 text-white/60 hover:bg-white/10"
                : activeTab === "comments"
                  ? "bg-divine-red text-white btn-glow font-cinzel rounded-full"
                  : "bg-white/5 text-white/60 hover:bg-white/10 font-cinzel rounded-full"
            }`}
          >
            <MessageCircle className="w-4 h-4" />
            Comment Ideas
          </button>
          <button
            data-testid="image-tab"
            onClick={() => { setActiveTab("image"); setResponse(""); }}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-cinzel transition-all ${
              activeTab === "image"
                ? "bg-divine-red text-white btn-glow"
                : "bg-white/5 text-white/60 hover:bg-white/10"
            }`}
          >
            <Image className="w-4 h-4" />
            Analyze Photo
          </button>
        </div>

        {/* Global Settings - Persists across all tabs */}
        <div className="mb-6 space-y-3">
          {/* Include My Data Toggle */}
          <div className="flex items-center gap-4 p-3 bg-white/5 rounded-lg border border-divine-gold/20">
            {/* User Avatar */}
            <div className="w-12 h-12 rounded-full bg-divine-gold/20 flex items-center justify-center overflow-hidden flex-shrink-0">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-6 h-6 text-divine-gold" />
              )}
            </div>
            
            <div className="flex-1">
              <label className="text-sm text-white/80 font-medium">
                Include My Data in Responses
              </label>
              <p className="text-xs text-white/50 mt-1">
                Use your profile and learning data for personalized advice
              </p>
            </div>
            
            <Switch
              checked={includeMyData}
              onCheckedChange={setIncludeMyData}
            />
          </div>
          
          {/* Eross Target Selector */}
          <div>
            <label className="text-sm text-white/80 flex items-center gap-2">
              <Target className="w-4 h-4 text-divine-gold" />
              Eross Target (Optional)
            </label>
            <Select value={selectedMatch} onValueChange={setSelectedMatch}>
              <SelectTrigger data-testid="advice-match-select" className="mt-2 bg-white/5 border-white/10">
                <SelectValue placeholder="Select a target for context..." />
              </SelectTrigger>
              <SelectContent className="bg-divine-black-light border-white/10">
                <SelectItem value="none">No specific target</SelectItem>
                {matches.map(match => (
                  <SelectItem key={match.match_id} value={match.match_id}>
                    <span className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-divine-gold/20 flex items-center justify-center text-xs text-divine-gold">
                        {match.name[0]}
                      </span>
                      {match.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedMatch && selectedMatch !== "none" && (
              <p className="text-xs text-divine-gold mt-1">
                <Target className="w-3 h-3 inline mr-1" />
                Eros will focus advice on {matches.find(m => m.match_id === selectedMatch)?.name}
              </p>
            )}
          </div>
        </div>

        <div className={adviceMessages.length > 0 && activeTab === "advice" ? "" : "grid md:grid-cols-2 gap-8"}>
          {/* Advice Chat Section - Full width when conversation active */}
          {activeTab === "advice" ? (
            adviceMessages.length > 0 ? (
              // Chat conversation view
              <div className={isCyber ? "cyber-card p-6" : "glass-gold rounded-xl p-6"}>
                {/* Header with New Conversation button */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {isCyber ? (
                      <div className="relative">
                        <div className="absolute inset-0 bg-cyan-400/30 blur-md rounded-full" />
                        <Brain className="w-6 h-6 text-cyan-400 relative" />
                      </div>
                    ) : (
                      <Heart className="w-6 h-6 text-divine-red fill-divine-red" />
                    )}
                    <h3 className={isCyber ? "text-lg text-white font-semibold" : "font-cinzel text-lg text-white"}>
                      {isCyber ? "Agent Eros Chat" : "Eros's Guidance"}
                    </h3>
                    <span className="text-xs text-white/40 px-2 py-0.5 bg-white/5 rounded">
                      {adviceMessages.length} messages
                    </span>
                  </div>
                  <Button
                    onClick={startNewAdviceSession}
                    variant="ghost"
                    size="sm"
                    className="text-white/60 hover:text-white hover:bg-white/10"
                    data-testid="new-conversation-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    New Chat
                  </Button>
                </div>

                {/* Messages container */}
                <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 mb-4">
                  {adviceMessages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                        msg.role === 'user' 
                          ? isCyber 
                            ? 'bg-gradient-to-r from-cyan-500/30 to-purple-500/30 border border-cyan-500/30' 
                            : 'bg-divine-gold/20 border border-divine-gold/30'
                          : 'bg-white/5 border border-white/10'
                      }`}>
                        {msg.role === 'user' ? (
                          <p className="text-white whitespace-pre-wrap">{msg.content}</p>
                        ) : (
                          <ResponseWithCopy content={msg.content} />
                        )}
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`animate-spin w-4 h-4 border-2 border-t-transparent rounded-full ${
                            isCyber ? "border-cyan-400" : "border-divine-gold"
                          }`} />
                          <span className="text-white/60 text-sm">
                            {isCyber ? "Processing..." : "Eros is contemplating..."}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input area */}
                <div className="flex gap-2">
                  <Textarea
                    ref={adviceInputRef}
                    data-testid="advice-input"
                    value={adviceQuestion}
                    onChange={(e) => setAdviceQuestion(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleGetAdvice();
                      }
                    }}
                    placeholder="Continue the conversation..."
                    className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[60px] max-h-[120px] resize-none"
                  />
                  <Button
                    data-testid="send-advice-btn"
                    onClick={handleGetAdvice}
                    disabled={loading || !adviceQuestion.trim()}
                    className={isCyber 
                      ? "h-auto bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white rounded-lg"
                      : "h-auto bg-divine-gold hover:bg-divine-gold-light text-black rounded-full px-4"
                    }
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            ) : (
              // Initial input view (no messages yet) - shows in grid
              <>
                <div className={isCyber ? "cyber-card p-6" : "glass-gold rounded-xl p-6"}>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 mb-4">
                      {isCyber ? (
                        <div className="relative">
                          <div className="absolute inset-0 bg-cyan-400/30 blur-md rounded-full" />
                          <Brain className="w-6 h-6 text-cyan-400 relative" />
                        </div>
                      ) : (
                        <Heart className="w-6 h-6 text-divine-red fill-divine-red" />
                      )}
                      <h3 className={isCyber ? "text-lg text-white font-semibold" : "font-cinzel text-lg text-white"}>
                        {isCyber ? "Ask Agent Eros" : "Ask Eros"}
                      </h3>
                    </div>
                    
                    <Textarea
                      ref={adviceInputRef}
                      data-testid="advice-input"
                      value={adviceQuestion}
                      onChange={(e) => setAdviceQuestion(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleGetAdvice();
                        }
                      }}
                      placeholder="What matters of the heart weigh on your mind? Ask about relationships, dating, love, or connection..."
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[200px]"
                    />
                    
                    <Button
                      data-testid="get-advice-btn"
                      onClick={handleGetAdvice}
                      disabled={loading}
                      className={isCyber 
                        ? "w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-semibold rounded-lg glow-effect border-0"
                        : "w-full bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel rounded-full"
                      }
                    >
                      {loading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        isCyber ? <Zap className="w-4 h-4 mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      {isCyber ? "Get Guidance" : "Seek Divine Wisdom"}
                    </Button>
                  </div>
                </div>

                {/* Response Section for initial view */}
                <div className={isCyber ? "cyber-card p-6" : "glass-gold rounded-xl p-6"}>
                  <div className="flex items-center gap-3 mb-6">
                    {isCyber ? (
                      <div className="relative">
                        <div className="absolute inset-0 bg-cyan-400/30 blur-md rounded-full" />
                        <Brain className="w-6 h-6 text-cyan-400 relative" />
                      </div>
                    ) : (
                      <Sparkles className="w-6 h-6 text-divine-gold" />
                    )}
                    <h3 className={isCyber ? "text-lg text-white font-semibold" : "font-cinzel text-lg text-white"}>
                      {isCyber ? "AI Response" : "Divine Response"}
                    </h3>
                  </div>
                  
                  {loading ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <div className={`animate-spin w-10 h-10 border-2 border-t-transparent rounded-full mb-4 ${
                        isCyber ? "border-cyan-400" : "border-divine-gold"
                      }`} />
                      <p className="text-white/40">
                        {isCyber ? "Processing..." : "Eros is contemplating..."}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      {isCyber ? (
                        <Brain className="w-12 h-12 text-white/20 mx-auto mb-4" />
                      ) : (
                        <Heart className="w-12 h-12 text-white/20 mx-auto mb-4" />
                      )}
                      <p className="text-white/40">
                        {isCyber ? "Your guidance will appear here" : "Your divine guidance will appear here"}
                      </p>
                      <p className="text-white/30 text-sm mt-2">
                        Session memory is enabled - continue the conversation!
                      </p>
                    </div>
                  )}
                </div>
              </>
            )
          ) : (
            // Other tabs (comments, image) - keep original grid layout
            <>
              {/* Input Section */}
              <div className={isCyber ? "cyber-card p-6" : "glass-gold rounded-xl p-6"}>
                {activeTab === "comments" ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <Lightbulb className="w-6 h-6 text-divine-gold" />
                  <h3 className="font-cinzel text-lg text-white">Comment Suggestions</h3>
                </div>
                
                <div>
                  <label className="text-sm text-white/80">Post Content</label>
                  <Textarea
                    ref={commentInputRef}
                    data-testid="post-content-input"
                    value={postContent}
                    onChange={(e) => setPostContent(e.target.value)}
                    placeholder="Paste the post or describe what they shared..."
                    className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[100px]"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-white/80">Platform</label>
                    <Select value={platform} onValueChange={setPlatform}>
                      <SelectTrigger data-testid="platform-select" className="mt-2 bg-white/5 border-white/10">
                        <SelectValue placeholder="Platform" />
                      </SelectTrigger>
                      <SelectContent className="bg-divine-black-light border-white/10">
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="twitter">Twitter / X</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                        <SelectItem value="linkedin">LinkedIn</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-sm text-white/80">Tone</label>
                      <button
                        onClick={() => setShowTonePresets(!showTonePresets)}
                        className="text-xs text-divine-gold hover:text-divine-gold-light"
                      >
                        {showTonePresets ? "Hide presets" : "Manage presets"}
                      </button>
                    </div>
                    <Select value={tone} onValueChange={setTone}>
                      <SelectTrigger data-testid="tone-select" className="mt-2 bg-white/5 border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-divine-black-light border-white/10 max-h-[300px]">
                        {/* Favorites section */}
                        {favoriteTones.length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs text-divine-gold font-cinzel">★ Favorites</div>
                            {availableTones.filter(t => favoriteTones.includes(t.value)).map(t => (
                              <SelectItem key={`fav-${t.value}`} value={t.value}>
                                <span className="flex items-center gap-2">
                                  <Star className="w-3 h-3 text-divine-gold fill-divine-gold" />
                                  {t.label}
                                  {t.custom && <Wand2 className="w-3 h-3 text-purple-400" />}
                                  {t.value === defaultTone && <span className="text-xs text-divine-gold">(default)</span>}
                                </span>
                              </SelectItem>
                            ))}
                            <div className="border-t border-white/10 my-1" />
                          </>
                        )}
                        {/* Custom tones section */}
                        {customTones.length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs text-purple-400 font-cinzel">✨ Custom Tones</div>
                            {availableTones.filter(t => t.custom).map(t => (
                              <SelectItem key={t.value} value={t.value}>
                                <span className="flex items-center gap-2">
                                  <Wand2 className="w-3 h-3 text-purple-400" />
                                  {t.label}
                                </span>
                              </SelectItem>
                            ))}
                            <div className="border-t border-white/10 my-1" />
                          </>
                        )}
                        {/* All built-in tones */}
                        <div className="px-2 py-1 text-xs text-white/40">Built-in Tones</div>
                        {TONE_OPTIONS.map(t => (
                          <SelectItem key={t.value} value={t.value}>
                            <span className="flex items-center gap-2">
                              {t.label}
                              {t.value === defaultTone && !favoriteTones.includes(t.value) && (
                                <span className="text-xs text-divine-gold">(default)</span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Tone Presets Panel */}
                {showTonePresets && (
                  <div className="p-4 bg-white/5 rounded-lg space-y-4 animate-fade-in">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-white/60">Click ★ to favorite, ✓ to set as default</p>
                      <Button
                        onClick={openCreateToneDialog}
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Create Custom Tone
                      </Button>
                    </div>
                    
                    {/* Custom Tones */}
                    {customTones.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-purple-400 font-cinzel">✨ Your Custom Tones</p>
                        {customTones.map(t => (
                          <div 
                            key={t.tone_id}
                            className={`flex items-center justify-between p-2 rounded-lg border ${
                              tone === t.tone_id ? "bg-purple-500/20 border-purple-500/30" : "bg-white/5 border-transparent"
                            }`}
                          >
                            <button
                              onClick={() => setTone(t.tone_id)}
                              className="flex-1 text-left"
                            >
                              <span className="text-sm text-white flex items-center gap-2">
                                <Wand2 className="w-3 h-3 text-purple-400" />
                                {t.name}
                              </span>
                              <span className="block text-xs text-white/40">{t.description}</span>
                            </button>
                            <div className="flex gap-1">
                              <button
                                onClick={() => toggleFavoriteTone(t.tone_id)}
                                className={`p-1 rounded ${favoriteTones.includes(t.tone_id) ? "text-divine-gold" : "text-white/30 hover:text-white/60"}`}
                                title="Favorite"
                              >
                                <Star className={`w-4 h-4 ${favoriteTones.includes(t.tone_id) ? "fill-divine-gold" : ""}`} />
                              </button>
                              <button
                                onClick={() => openEditToneDialog(t)}
                                className="p-1 rounded text-white/30 hover:text-white/60"
                                title="Edit"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteCustomTone(t.tone_id)}
                                className="p-1 rounded text-white/30 hover:text-red-500"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Built-in Tones */}
                    <div className="space-y-2">
                      <p className="text-xs text-white/40">Built-in Tones</p>
                      <div className="grid grid-cols-2 gap-2">
                        {TONE_OPTIONS.map(t => (
                          <div 
                            key={t.value}
                            className={`flex items-center justify-between p-2 rounded-lg ${
                              tone === t.value ? "bg-divine-gold/20 border border-divine-gold/30" : "bg-white/5"
                            }`}
                          >
                            <button
                              onClick={() => setTone(t.value)}
                              className="flex-1 text-left"
                            >
                              <span className="text-sm text-white">{t.label}</span>
                              <span className="block text-xs text-white/40">{t.description}</span>
                            </button>
                            <div className="flex gap-1">
                              <button
                                onClick={() => toggleFavoriteTone(t.value)}
                                className={`p-1 rounded ${favoriteTones.includes(t.value) ? "text-divine-gold" : "text-white/30 hover:text-white/60"}`}
                                title={favoriteTones.includes(t.value) ? "Remove from favorites" : "Add to favorites"}
                              >
                                <Star className={`w-4 h-4 ${favoriteTones.includes(t.value) ? "fill-divine-gold" : ""}`} />
                              </button>
                              <button
                                onClick={() => setAsDefaultTone(t.value)}
                                className={`p-1 rounded ${defaultTone === t.value ? "text-green-500" : "text-white/30 hover:text-white/60"}`}
                                title="Set as default"
                              >
                                <Check className={`w-4 h-4 ${defaultTone === t.value ? "text-green-500" : ""}`} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                <Button
                  data-testid="get-comments-btn"
                  onClick={handleGetCommentSuggestions}
                  disabled={loading}
                  className={isCyber 
                    ? "w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-semibold rounded-lg glow-effect border-0"
                    : "w-full bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel rounded-full"
                  }
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Generate Comments
                </Button>
              </div>
            ) : (
              /* Image Analysis Tab */
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <Image className="w-6 h-6 text-divine-gold" />
                  <h3 className="font-cinzel text-lg text-white">Analyze Photo</h3>
                </div>
                
                {/* Match Selector */}
                <div>
                  <label className="text-sm text-white/80">Whose photo is this?</label>
                  <Select value={selectedMatch} onValueChange={setSelectedMatch}>
                    <SelectTrigger data-testid="image-match-select" className="mt-2 bg-white/5 border-white/10">
                      <SelectValue placeholder="Select a match..." />
                    </SelectTrigger>
                    <SelectContent className="bg-divine-black-light border-white/10">
                      <SelectItem value="none">Unknown / General</SelectItem>
                      {matches.map(match => (
                        <SelectItem key={match.match_id} value={match.match_id}>
                          <span className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-divine-gold/20 flex items-center justify-center text-xs text-divine-gold">
                              {match.name[0]}
                            </span>
                            {match.name}
                            {match.platform && <span className="text-white/40 text-xs">({match.platform})</span>}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedMatch && selectedMatch !== "none" && (
                    <p className="text-xs text-divine-gold mt-1">
                      Eros will personalize comments for {matches.find(m => m.match_id === selectedMatch)?.name}
                    </p>
                  )}
                </div>
                
                <p className="text-white/60 text-sm">
                  Upload a photo or paste an image URL for Eros to analyze and suggest comments.
                </p>

                {/* Image Upload Area */}
                {!imagePreview && !imageUrl ? (
                  <div 
                    className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center hover:border-divine-gold/50 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-10 h-10 text-white/40 mx-auto mb-3" />
                    <p className="text-white/60 mb-2">Click to upload an image</p>
                    <p className="text-white/40 text-xs">PNG, JPG, WEBP up to 10MB</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      data-testid="image-upload-input"
                    />
                  </div>
                ) : (
                  <div className="relative">
                    <img 
                      src={imagePreview || imageUrl} 
                      alt="Preview" 
                      className="w-full max-h-64 object-contain rounded-xl border border-white/10"
                    />
                    <Button
                      onClick={clearImage}
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {/* Or use URL */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-3 bg-divine-black-light text-white/40">or paste image URL</span>
                  </div>
                </div>

                <Input
                  data-testid="image-url-input"
                  value={imageUrl}
                  onChange={(e) => {
                    setImageUrl(e.target.value);
                    setImagePreview(e.target.value);
                    setImageBase64("");
                  }}
                  placeholder="https://example.com/image.jpg"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                />

                {/* Context */}
                <div>
                  <label className="text-sm text-white/80">Context (Optional)</label>
                  <Textarea
                    data-testid="image-context-input"
                    value={imageContext}
                    onChange={(e) => setImageContext(e.target.value)}
                    placeholder="Any additional context about the photo or the person who posted it..."
                    className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[60px]"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-white/80">Platform</label>
                    <Select value={platform} onValueChange={setPlatform}>
                      <SelectTrigger className="mt-2 bg-white/5 border-white/10">
                        <SelectValue placeholder="Platform" />
                      </SelectTrigger>
                      <SelectContent className="bg-divine-black-light border-white/10">
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="twitter">Twitter / X</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                        <SelectItem value="linkedin">LinkedIn</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm text-white/80">Tone</label>
                    <Select value={tone} onValueChange={setTone}>
                      <SelectTrigger className="mt-2 bg-white/5 border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-divine-black-light border-white/10 max-h-[300px]">
                        {/* Favorites section */}
                        {favoriteTones.length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs text-divine-gold font-cinzel">★ Favorites</div>
                            {TONE_OPTIONS.filter(t => favoriteTones.includes(t.value)).map(t => (
                              <SelectItem key={`fav-img-${t.value}`} value={t.value}>
                                <span className="flex items-center gap-2">
                                  <Star className="w-3 h-3 text-divine-gold fill-divine-gold" />
                                  {t.label}
                                </span>
                              </SelectItem>
                            ))}
                            <div className="border-t border-white/10 my-1" />
                          </>
                        )}
                        {/* All tones */}
                        {TONE_OPTIONS.map(t => (
                          <SelectItem key={`img-${t.value}`} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <Button
                  data-testid="analyze-image-btn"
                  onClick={handleAnalyzeImage}
                  disabled={loading || (!imageBase64 && !imageUrl)}
                  className={isCyber 
                    ? "w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-semibold rounded-lg glow-effect border-0"
                    : "w-full bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel rounded-full"
                  }
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    isCyber ? <Zap className="w-4 h-4 mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Analyze & Suggest Comments
                </Button>
              </div>
            )}
          </div>

          {/* Response Section for comments/image tabs */}
          <div className={isCyber ? "cyber-card p-6" : "glass-gold rounded-xl p-6"}>
            <div className="flex items-center gap-3 mb-6">
              {isCyber ? (
                <div className="relative">
                  <div className="absolute inset-0 bg-cyan-400/30 blur-md rounded-full" />
                  <Brain className="w-6 h-6 text-cyan-400 relative" />
                </div>
              ) : (
                <Sparkles className="w-6 h-6 text-divine-gold" />
              )}
              <h3 className={isCyber ? "text-lg text-white font-semibold" : "font-cinzel text-lg text-white"}>
                {isCyber ? "AI Response" : "Divine Response"}
              </h3>
            </div>
            
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className={`animate-spin w-10 h-10 border-2 border-t-transparent rounded-full mb-4 ${
                  isCyber ? "border-cyan-400" : "border-divine-gold"
                }`} />
                <p className="text-white/40">
                  {activeTab === "image" 
                    ? isCyber ? "Analyzing image..." : "Eros is studying the image..." 
                    : isCyber ? "Processing..." : "Eros is contemplating..."
                  }
                </p>
              </div>
            ) : response ? (
              <ResponseWithCopy content={response} />
            ) : (
              <div className="text-center py-16">
                {activeTab === "image" ? (
                  isCyber ? (
                    <Brain className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  ) : (
                    <Image className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  )
                ) : (
                  isCyber ? (
                    <Brain className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  ) : (
                    <Heart className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  )
                )}
                <p className="text-white/40">
                  {activeTab === "image"
                    ? "Image analysis and suggestions will appear here"
                    : "Comment suggestions will appear here"
                  }
                </p>
              </div>
            )}
          </div>
            </>
          )}
        </div>

        {/* Quick Tips */}
        <div className="mt-8 glass rounded-xl p-6">
          <h4 className="font-cinzel text-lg text-white mb-4">Eros&apos;s Quick Tips</h4>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-divine-gold font-medium mb-2">Be Authentic</p>
              <p className="text-white/60 text-sm">Genuine connections start with genuine words. Let your personality shine.</p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-divine-gold font-medium mb-2">Show Interest</p>
              <p className="text-white/60 text-sm">Ask questions and engage with their content meaningfully.</p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-divine-gold font-medium mb-2">Be Patient</p>
              <p className="text-white/60 text-sm">Love grows over time. Don&apos;t rush the divine process.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// Component to render response with copy buttons for structured content
const ResponseWithCopy = ({ content }) => {
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Copied to clipboard!");
    }).catch(() => {
      toast.error("Failed to copy");
    });
  };

  // Parse content to find [CC> and <CC] copy-paste blocks
  const parseContent = () => {
    const blocks = [];
    
    // Regex to find [CC> ... <CC] blocks
    const ccPattern = /\[CC>([\s\S]*?)<CC\]/g;
    
    let lastIndex = 0;
    let match;
    
    while ((match = ccPattern.exec(content)) !== null) {
      // Add text before the CC block
      if (match.index > lastIndex) {
        const textBefore = content.slice(lastIndex, match.index).trim();
        if (textBefore) {
          blocks.push({ type: 'text', content: textBefore });
        }
      }
      
      // Add the copyable block (content between [CC> and <CC])
      const copyableContent = match[1].trim();
      if (copyableContent) {
        blocks.push({ type: 'copyable', content: copyableContent });
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text after last CC block
    if (lastIndex < content.length) {
      const remainingText = content.slice(lastIndex).trim();
      if (remainingText) {
        blocks.push({ type: 'text', content: remainingText });
      }
    }
    
    // If no CC blocks found, return the entire content as text
    if (blocks.length === 0) {
      blocks.push({ type: 'text', content: content });
    }
    
    return blocks;
  };

  const blocks = parseContent();

  return (
    <div className="space-y-2 animate-fade-in">
      {blocks.map((block, index) => {
        if (block.type === 'copyable') {
          return (
            <div key={index} className="relative group my-3">
              <div className="bg-white/5 border border-divine-gold/30 rounded-lg p-4 pr-14 hover:bg-white/10 transition-colors">
                <div className="text-white/90 leading-relaxed whitespace-pre-wrap">
                  {block.content}
                </div>
              </div>
              <button
                onClick={() => handleCopy(block.content)}
                className="absolute right-3 top-3 p-2 bg-divine-gold/20 hover:bg-divine-gold/40 rounded-md transition-all opacity-60 group-hover:opacity-100"
                title="Copy to clipboard"
              >
                <Copy className="w-4 h-4 text-divine-gold" />
              </button>
            </div>
          );
        }
        
        // Regular text - render with markdown-like formatting
        return (
          <div key={index} className="text-white/80 leading-relaxed whitespace-pre-wrap">
            {block.content}
          </div>
        );
      })}
    </div>
  );
};

export default ErosAdvice;
